'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as prismic from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { useIsMobile, usePrefersReducedMotion } from '@/hooks';
import { chunkArray } from '@/utils';

export interface CardSliderItem {
  icon: prismic.ImageField<string>;
  title: prismic.KeyTextField;
  heading: prismic.KeyTextField;
  description: prismic.KeyTextField;
  link: prismic.LinkField;
  link_label: prismic.KeyTextField;
  company: prismic.ImageField<string>;
  background: prismic.ImageField<string>;
}

export interface CardSliderProps {
  title: string | Promise<string>;
  slides: CardSliderItem[];
}

export default function CardSlider({ title, slides }: CardSliderProps) {
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();
  // Pause auto-advance on hover/focus (WCAG 2.2.2). Reduced-motion users never
  // auto-advance at all.
  const [isPaused, setIsPaused] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeMobileCard, setActiveMobileCard] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derive the display title directly from `title` during render. `title` may be
  // a string or (for Prismic rich text) an array of blocks; a Promise yields an
  // empty title, matching the original effect-based behavior.
  const displayTitle = useMemo<string>(() => {
    if (typeof title === 'string') {
      return title;
    }
    if (Array.isArray(title)) {
      // Handle Prismic rich text format
      return (title as Array<{ text: string }>)
        .map((block) => block.text)
        .join(' ');
    }
    return '';
  }, [title]);

  const preparedSlides = useMemo(
    () => (isMobile ? slides.map((slide) => [slide]) : chunkArray(slides, 3)),
    [isMobile, slides],
  );
  const slideIndexes = preparedSlides.map((_, index) => index);

  const restartInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    // Don't auto-advance for reduced-motion users or while paused (hover/focus).
    if (prefersReducedMotion || isPaused) {
      return;
    }
    intervalRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % preparedSlides.length);
      setActiveMobileCard(0);
    }, 10000);
  }, [preparedSlides.length, prefersReducedMotion, isPaused]);

  useEffect(() => {
    restartInterval();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [preparedSlides.length, restartInterval]);

  const handleDotClick = (index: number) => {
    setActiveSlide(index);
    setActiveMobileCard(0);
    restartInterval();
  };

  const handlePrevCard = () => {
    setActiveMobileCard((prev) => {
      if (prev === 0) {
        const prevSlide =
          (activeSlide - 1 + preparedSlides.length) % preparedSlides.length;
        setActiveSlide(prevSlide);
        return preparedSlides[prevSlide].length - 1;
      }
      return prev - 1;
    });
    restartInterval();
  };

  const handleNextCard = () => {
    setActiveMobileCard((prev) => {
      const currentSlideCards = preparedSlides[activeSlide];
      if (prev === currentSlideCards.length - 1) {
        const nextSlide = (activeSlide + 1) % preparedSlides.length;
        setActiveSlide(nextSlide);
        return 0;
      }
      return prev + 1;
    });
    restartInterval();
  };

  const renderDesktop = () => {
    return (
      <div className="hidden md:block w-full relative h-72">
        {preparedSlides.map((slide, slideIndex) => (
          <div
            key={slideIndex}
            className={`absolute top-0 left-0 w-full h-full inline-flex justify-start items-start transition-opacity duration-500 ${
              activeSlide === slideIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {slide.map((caseStudy: CardSliderItem, cardIndex: number) => (
              <div
                key={cardIndex}
                data-property-1="Default"
                className="group cursor-pointer flex-1 h-full outline outline-2 outline-offset-[-1px] outline-white inline-flex flex-col justify-start items-start overflow-hidden relative"
              >
                {/* Background image that appears on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <PrismicNextImage
                    fill
                    className="object-cover"
                    field={caseStudy.background}
                  />
                  <div className="absolute inset-0 bg-white/90 transition-colors duration-300" />
                </div>

                <div className="self-stretch border-1.5 flex-1 p-6 flex flex-col justify-between items-start overflow-hidden relative z-10 bg-transparent group-hover:bg-transparent transition-colors duration-300">
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch group-hover:text-black justify-center text-white text-2xl font-['Manrope'] leading-loose">
                      {caseStudy.title}
                    </div>
                    <div className="self-stretch flex flex-col justify-center items-start gap-2">
                      <div className="self-stretch inline-flex justify-start items-center gap-2">
                        <div className="w-8 h-12 relative overflow-hidden">
                          <div className="w-6 h-6 absolute left-[2px] top-[12px] text-sky-300">
                            <PrismicNextImage
                              field={caseStudy.icon}
                              fallbackAlt=""
                            />
                          </div>
                        </div>
                        <div className="flex justify-center items-center gap-2">
                          <div
                            className={`justify-start text-white group-hover:text-black text-4xl font-bold leading-[46px]`}
                          >
                            {caseStudy.heading}
                          </div>
                          <div className="w-40 justify-end text-white group-hover:text-black text-sm font-bold leading-tight">
                            {caseStudy.description}
                          </div>
                        </div>
                      </div>
                      <div
                        data-dark-mode="False"
                        data-icon-position="No icon"
                        data-small="False"
                        data-style="Link"
                        className="rounded-tl-2xl rounded-tr rounded-bl-2xl rounded-br-2xl inline-flex justify-center items-center gap-2"
                      >
                        <div className="group-hover:text-black justify-start text-teal-500 text-base font-bold font-['Manrope'] leading-normal inline-flex items-center gap-1">
                          {caseStudy.link_label}
                          <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-7">
                    <div className="brightness-200 contrast-200">
                      <PrismicNextImage
                        width={100}
                        height={100}
                        field={caseStudy.company}
                        fallbackAlt=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderMobile = () => {
    return (
      <div className="md:hidden w-full relative h-72">
        <div className="absolute inset-0 flex items-center justify-between z-20 px-2 pointer-events-none">
          <button
            onClick={handlePrevCard}
            className="bg-black/30 hover:bg-black/50 text-white rounded-full p-2 focus:outline-none pointer-events-auto"
            aria-label="Previous case study"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleNextCard}
            className="bg-black/30 hover:bg-black/50 text-white rounded-full p-2 focus:outline-none pointer-events-auto"
            aria-label="Next case study"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {preparedSlides.map((slide, slideIndex) => (
          <div
            key={slideIndex}
            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-500 ${
              activeSlide === slideIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {slide.map((caseStudy: CardSliderItem, cardIndex: number) => (
              <div
                key={cardIndex}
                data-property-1="Default"
                className={`group w-full h-full outline outline-2 outline-offset-[-1px] outline-white flex flex-col justify-start items-start overflow-hidden relative transition-opacity duration-500 ${
                  activeMobileCard === cardIndex
                    ? 'opacity-100 z-10'
                    : 'opacity-0 z-0'
                }`}
              >
                <div className="self-stretch flex-1 p-6 flex flex-col justify-between items-start overflow-hidden relative z-10 bg-transparent group-hover:bg-transparent transition-colors duration-300">
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch justify-center text-white text-2xl font-['Manrope'] leading-loose">
                      <PrismicNextLink field={caseStudy.link}>
                        {caseStudy.title}
                      </PrismicNextLink>
                    </div>
                    <div className="self-stretch flex flex-col justify-center items-start gap-2">
                      <div className="self-stretch inline-flex justify-start items-center gap-2">
                        <div className="w-8 h-12 relative overflow-hidden">
                          <PrismicNextImage
                            field={caseStudy.icon}
                            fallbackAlt=""
                          />
                        </div>
                        <div className="flex justify-center items-center gap-2">
                          <div
                            className={`justify-start text-white text-4xl font-bold font-['Manrope'] leading-[48px]`}
                          >
                            <PrismicNextLink field={caseStudy.link}>
                              {caseStudy.heading}
                            </PrismicNextLink>
                          </div>
                          <div className="w-40 justify-end text-white text-sm font-bold font-['Manrope'] leading-tight">
                            {caseStudy.description}
                          </div>
                        </div>
                      </div>
                      <div
                        data-dark-mode="False"
                        data-icon-position="No icon"
                        data-small="False"
                        data-style="Link"
                        className="rounded-tl-2xl rounded-tr rounded-bl-2xl rounded-br-2xl inline-flex justify-center items-center gap-2"
                      >
                        <div className="group-hover:text-black justify-start text-teal-500 text-base font-bold font-['Manrope'] leading-normal inline-flex items-center gap-1 cursor-pointer pointer-events-auto ml-10">
                          {caseStudy.link_label}
                          <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-7">
                    <div className="brightness-200 contrast-200">
                      <PrismicNextImage
                        width={100}
                        height={100}
                        field={caseStudy.company}
                        fallbackAlt=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderDots = () => {
    return (
      <div className="py-2 md:py-4 flex justify-center items-center gap-4">
        {slideIndexes.map((index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full cursor-pointer ${
              activeSlide === index ? 'bg-white' : 'border border-white'
            }`}
            onClick={() => handleDotClick(index)}
            role="button"
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-8">
        <h1 className="text-2xl md:text-[40px] font-bold text-white leading-tight">
          {displayTitle}
        </h1>
        {renderDots()}
      </div>

      <div
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocusCapture={() => setIsPaused(true)}
        onBlurCapture={() => setIsPaused(false)}
      >
        {!isMobile ? renderDesktop() : renderMobile()}
      </div>
    </>
  );
}
